import { useEffect, useState } from 'react';
import { Sparkles, Shield, Gift, ShoppingBag } from 'lucide-react';
import { UploadedMethodType, useOnboardingStore } from '../../../store/onboardingStore';
import { Toggle } from '../ui/Toggle';
import { axiosInstanceConversationalEngine } from '../../../services/instance';
import Loader from '../../common/Loader';
import { dictionary } from '../../../utils/dictionary';
import { useAuth } from '../../../context/AuthContext';
import { VisualStyle } from '../../../types/onboarding';

export function StepBrandAndPrice() {
  const {
    data,
    updateVisualStyle,
    updateProductData,
    updateAdditionalOffers,
    priceErrors,
    setPriceErrors,
    isFetchingTemplates,
    isFetchingAdTemplates,
    sectionTemplates,
    adTemplates,
    fetchSectionTemplates,
    fetchAdTemplates,
    rankOnboardingTemplates,
  } = useOnboardingStore();
  const { visualStyle, productData } = data;
  const { language } = useAuth();
  const [brandName, setBrandName] = useState(visualStyle.brandName || '');
  const [mainPrice, setMainPrice] = useState(productData.price || '');
  const [bumpOfferPrice, setBumpOfferPrice] = useState(data.additionalOffers.bumpOffer?.price || '');
  const [warrantyEnabled, setWarrantyEnabled] = useState(productData.warranty !== 'none');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPriceInfo, setShowPriceInfo] = useState(false);
  const [showBumpPriceInfo, setShowBumpPriceInfo] = useState(false);
  const [showUpsellPriceInfo, setShowUpsellPriceInfo] = useState<string | null>(null);
  const [brandNameSuggestions, setBrandNameSuggestions] = useState<string[]>([]);
  const [currentSuggestionIndex, setCurrentSuggestionIndex] = useState(0);
  const [brandGenerationError, setBrandGenerationError] = useState<string | null>(null);

  const brandErrorMessage =
    language === 'en'
      ? "We couldn't generate brand names. Try again."
      : language === 'pt'
        ? "Nao conseguimos gerar nomes de marca. Tente novamente."
        : "No pudimos generar nombres de marca. Intenta de nuevo.";

  const missingProductContextMessage =
    language === 'en'
      ? "Select or complete a product before generating a brand name."
      : language === 'pt'
        ? "Selecione ou complete um produto antes de gerar um nome de marca."
        : "Selecciona o completa un producto antes de generar un nombre de marca.";

  const fetchSuggestedBrandNames = async () => {
    try {
      const productName = data.productData?.name?.trim() || "";
      const productDescription = data.productData?.details?.trim() || "";

      if (!productName || !productDescription) {
        setBrandGenerationError(missingProductContextMessage);
        return [];
      }

      const response = await axiosInstanceConversationalEngine.post('/brand-suggestions/generate', {
        product_name: productName.split(' ').slice(0, 5).join(' '),
        product_description: productDescription,
        language,

      });

      if (response.data.brand_names && response.data.brand_names.length > 0) {
        setBrandGenerationError(null);
        return response.data.brand_names;
      }

      setBrandGenerationError(brandErrorMessage);
      return [];
    } catch (error) {
      console.error("Error fetching brand names:", error);
      setBrandGenerationError(brandErrorMessage);
      return [];
    }
  };


  const loadBrandNames = async () => {
    if (isGenerating) return;

    setIsGenerating(true);

    try {
      const suggestions = await fetchSuggestedBrandNames();
      setBrandNameSuggestions(suggestions);

      if (suggestions.length === 0) {
        return;
      }

      const updates: Partial<VisualStyle> = {};

      if (suggestions.length > 0) {
        updates.brandName = suggestions[0];
        setBrandName(suggestions[0]);
      }

      if (visualStyle.colorPalette === 'custom') {
        updates.colorPalette = 'professional-blue';
      }

      if (Object.keys(updates).length > 0) {
        updateVisualStyle(updates);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateBrandName = () => {
    if (isGenerating) {
      return;
    }

    if (brandNameSuggestions.length === 0) {
      return loadBrandNames();
    }

    setIsGenerating(true);

    try {
      const nextIndex = (currentSuggestionIndex + 1) % brandNameSuggestions.length;
      const newName = brandNameSuggestions[nextIndex];

      setBrandGenerationError(null);
      setBrandName(newName);
      updateVisualStyle({
        brandName: newName
      });

      setCurrentSuggestionIndex(nextIndex);
    } catch (error) {
      console.error("Error cycling brand name:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleBrandNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBrandGenerationError(null);
    setBrandName(value);
    updateVisualStyle({ brandName: value });
  };

  const handleMainPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMainPrice(value);
    updateProductData({ ...productData, price: value });
    if (priceErrors.main) setPriceErrors({ ...priceErrors, main: undefined });
  };

  const handleBumpOfferPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBumpOfferPrice(value);

    if (data.additionalOffers.bumpOffer) {
      updateAdditionalOffers({
        ...data.additionalOffers,
        bumpOffer: {
          ...data.additionalOffers.bumpOffer,
          price: value
        }
      });
    }
    if (priceErrors.bump) setPriceErrors({ ...priceErrors, bump: undefined });
  };

  const isPreviousProduct = productData.uploadedMethod === UploadedMethodType.PREVIOUS;
  const isPreviousBumpOffer = data.additionalOffers.bumpOffer?.uploadedMethod === UploadedMethodType.PREVIOUS;

  useEffect(() => {
    const nextWarranty = productData.warranty || '30days';
    setWarrantyEnabled(nextWarranty !== 'none');
    updateProductData({
      ...productData,
      warranty: nextWarranty,
      customWarrantyDays: nextWarranty === 'none' ? undefined : productData.customWarrantyDays || '30'
    });
  }, []);

  useEffect(() => {
    fetchSectionTemplates();
    fetchAdTemplates();
  }, [fetchSectionTemplates, fetchAdTemplates]);

  useEffect(() => {
    const canRankPages = !isFetchingTemplates && sectionTemplates.length > 0;
    const canRankAds = !isFetchingAdTemplates && adTemplates.length > 0;
    if (canRankPages || canRankAds) {
      rankOnboardingTemplates();
    }
  }, [isFetchingTemplates, isFetchingAdTemplates, sectionTemplates.length, adTemplates.length, rankOnboardingTemplates]);

  const handleWarrantyToggle = (enabled: boolean) => {
    setWarrantyEnabled(enabled);
    updateProductData({
      ...productData,
      warranty: enabled ? '30days' : 'none',
      customWarrantyDays: enabled ? '30' : undefined
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {dictionary["brand-price-warranty"]?.[language] ?? "Brand, price and warranty"}
        </h1>
      </div>

      <div className="max-w-2xl mx-auto space-y-3">

        <div>
          <label className="block font-extrabold text-sm text-[#1E293B] mb-1">
            {dictionary["brand-name"][language]}
          </label>
          <div className="flex gap-3">
            <input
              type="text"
              autoFocus
              value={brandName}
              disabled={isGenerating}
              onChange={handleBrandNameChange}
              placeholder={dictionary["enter-your-brand-name-placeholder"][language]}
              className="rounded-[16px] text-xs flex-1 px-4 py-2 border border-[#E2E8F0] focus:border-[#3B4A9F] focus:outline-none focus:ring-1 focus:ring-[#3B4A9F]"
            />
            <button
              onClick={handleGenerateBrandName}
              disabled={isGenerating}
              className="rounded-[16px] px-6 py-2 bg-[#F8FAFC] border text-[#3B4A9F] hover:bg-gray-100 font-medium transition-colors flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isGenerating ? <Loader size={20} /> : <Sparkles className="w-5 h-5" />}
              {dictionary["generate-with-ai"][language]}
            </button>
          </div>
          {brandGenerationError && (
            <p className="mt-2 text-xs text-red-500">
              {brandGenerationError}
            </p>
          )}
        </div>

        <div className="bg-[#FCFCFD] rounded-[18px] border border-[#E2E8F0] p-2">
          <div className={`flex gap-4 ${priceErrors.main ? 'items-start' : 'items-center'}`}>
            <div className={`w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden ${priceErrors.main ? 'mt-1' : ''}`}>
              {data.visualContent.imagesBase64[0] || data.visualContent.images[0] ? (
                <img src={data.visualContent.imagesBase64[0] || data.visualContent.images[0]} alt={productData.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200" />
              )}
            </div>
            <div className={`flex-1 min-w-0 ${priceErrors.main ? 'mt-1' : ''}`}>
              <h3 className="text-sm font-medium text-gray-900 truncate">
                {productData.name || dictionary["main-product"][language]}
              </h3>
              <span className="inline-block mt-1 px-2 py-0.5 bg-[#F1F5F9] text-[#64748B] text-xs font-medium rounded">
                {dictionary["main-product"][language]}
              </span>
            </div>
            <div className="flex flex-col items-stretch gap-1">
              <div className="relative w-32" onClick={isPreviousProduct ? () => setShowPriceInfo(true) : undefined}>
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={mainPrice}
                  onChange={handleMainPriceChange}
                  placeholder="50000"
                  disabled={isPreviousProduct}
                  className={`w-full pl-7 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-1 text-right placeholder:text-gray-300 ${
                    isPreviousProduct
                      ? 'bg-gray-100 text-gray-500 cursor-pointer'
                      : priceErrors.main
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:border-[#3B4A9F] focus:ring-[#3B4A9F]'
                  }`}
                />
              </div>
              {isPreviousProduct && showPriceInfo && (
                <p className="text-xs text-amber-600 bg-amber-50 px-2 py-1.5 rounded-lg max-w-xs">
                  {dictionary["price-edit-in-products"]?.[language] ?? "Para cambiar el precio, edita el producto en la sección de Productos"}
                </p>
              )}
              {priceErrors.main && !isPreviousProduct && (
                <p className="text-xs text-red-500 text-right">
                  {priceErrors.main}
                </p>
              )}
            </div>
          </div>
        </div>

        {data.additionalOffers.bumpOffer?.enabled && data.additionalOffers.bumpOffer?.productName && (
          <div className="bg-[#F6FFFA] rounded-[18px] border border-[#E3FBEB] p-2">
            <div className={`flex gap-4 ${priceErrors.bump ? 'items-start' : 'items-center'}`}>
              <div className={`w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden ${priceErrors.bump ? 'mt-1' : ''}`}>
                {data.additionalOffers.bumpOffer.url_images || data.additionalOffers.bumpOffer.image ? (
                  <img src={data.additionalOffers.bumpOffer.url_images || data.additionalOffers.bumpOffer.image} alt={data.additionalOffers.bumpOffer.productName} className="w-full h-full object-cover border-[1px] border-[#E3FBEB] rounded-xl" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-green-100 to-green-200" />
                )}
              </div>
              <div className={`flex-1 min-w-0 ${priceErrors.bump ? 'mt-1' : ''}`}>
                <h3 className="font-medium text-[#15803D] truncate">
                  {data.additionalOffers.bumpOffer.productName || dictionary["order-bump"][language]}
                </h3>
                <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-[#E3FBEB] text-[#15803D] text-xs font-medium rounded">
                  <Gift className="w-4 h-4 text-green-800" />
                  {dictionary["order-bump"][language]}
                </span>
              </div>
              <div className="w-32 flex flex-col items-stretch gap-1">
                <div className="relative" onClick={isPreviousBumpOffer ? () => setShowBumpPriceInfo(true) : undefined}>
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={bumpOfferPrice}
                    onChange={handleBumpOfferPriceChange}
                    placeholder="12000"
                    disabled={isPreviousBumpOffer}
                    className={`w-full pl-7 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-1 text-right ${
                      isPreviousBumpOffer
                        ? 'bg-gray-100 text-gray-500 cursor-pointer border-gray-300'
                        : priceErrors.bump
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:border-[#3B4A9F] focus:ring-[#3B4A9F]'
                    }`}
                  />
                </div>
                {isPreviousBumpOffer && showBumpPriceInfo && (
                  <p className="text-xs text-amber-600 bg-amber-50 px-2 py-1.5 rounded-lg max-w-xs">
                    {dictionary["price-edit-in-products"]?.[language] ?? "Para cambiar el precio, edita el producto en la sección de Productos"}
                  </p>
                )}
                {priceErrors.bump && !isPreviousBumpOffer && (
                  <p className="text-xs text-red-500 text-right">
                    {priceErrors.bump}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {data.additionalOffers.upsell?.enabled && data.additionalOffers.upsell.products.map((upsell) => (
          <div key={upsell.id} className="bg-[#FBFAFF] rounded-[18px] border border-[#EFECFF] p-2">
            <div className={`flex gap-4 ${priceErrors.upsell ? 'items-start' : 'items-center'}`}>
              <div className={`w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden ${priceErrors.upsell ? 'mt-1' : ''}`}>
                {upsell.url_images?.[0] || upsell.image ? (
                  <img src={upsell.url_images?.[0] || upsell.image || ''} alt={upsell.productName} className="w-full h-full object-cover border-[1px] border-[#EFECFF] rounded-xl" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-purple-100 to-purple-200" />
                )}
              </div>
              <div className={`flex-1 min-w-0 ${priceErrors.upsell ? 'mt-1' : ''}`}>
                <h3 className="font-medium text-[#5B20B6] truncate">
                  {upsell.productName || `${dictionary["upsell-title"][language]}`}
                </h3>
                <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-[#EDE9FE] text-[#5B20B6] text-xs font-medium rounded">
                  <ShoppingBag className="w-4 h-4 text-[#6D29D9]" />
                  {dictionary["upsell-title"][language]}
                </span>
              </div>
              <div className="w-32 flex flex-col items-stretch gap-1">
                <div
                  className="relative"
                  onClick={upsell.uploadedMethod === UploadedMethodType.PREVIOUS ? () => setShowUpsellPriceInfo(upsell.id) : undefined}
                >
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={upsell.price}
                    onChange={(e) => {
                      const updatedProducts = data.additionalOffers.upsell.products.map(p =>
                        p.id === upsell.id ? { ...p, price: e.target.value } : p
                      );
                      updateAdditionalOffers({
                        ...data.additionalOffers,
                        upsell: {
                          ...data.additionalOffers.upsell,
                          products: updatedProducts
                        }
                      });
                      if (priceErrors.upsell) setPriceErrors({ ...priceErrors, upsell: undefined });
                    }}
                    placeholder="25000"
                    disabled={upsell.uploadedMethod === UploadedMethodType.PREVIOUS}
                    className={`w-full pl-7 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-1 text-right ${
                      upsell.uploadedMethod === UploadedMethodType.PREVIOUS
                        ? 'bg-gray-100 text-gray-500 cursor-pointer border-gray-300'
                        : priceErrors.upsell
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:border-[#3B4A9F] focus:ring-[#3B4A9F]'
                    }`}
                  />
                </div>
                {upsell.uploadedMethod === UploadedMethodType.PREVIOUS && showUpsellPriceInfo === upsell.id && (
                  <p className="text-xs text-amber-600 bg-amber-50 px-2 py-1.5 rounded-lg max-w-xs">
                    {dictionary["price-edit-in-products"]?.[language] ?? "Para cambiar el precio, edita el producto en la sección de Productos"}
                  </p>
                )}
                {priceErrors.upsell && upsell.uploadedMethod !== UploadedMethodType.PREVIOUS && (
                  <p className="text-xs text-red-500 text-right">
                    {priceErrors.upsell}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}


        <div
          className="rounded-[16px] px-4 py-3 mt-6 border border-gray-200 bg-[#FCFCFD] flex justify-between items-center transition-all duration-200"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 shadow-[0_0_10px_rgba(0,0,0,0.08)]">
              <Shield className="w-5 h-5 text-gray-500" />
            </div>

            <span className="font-medium text-gray-900">
              {dictionary["warranty"][language]}
            </span>
          </div>

          <div className="flex items-center gap-3">
            {warrantyEnabled && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">{dictionary["days"][language]}:</span>
                <input
                  type="number"
                  value={productData.customWarrantyDays ?? ''}
                  onChange={(e) => {
                    updateProductData({
                      ...productData,
                      warranty: 'custom',
                      customWarrantyDays: e.target.value
                    });
                  }}
                  className="w-20 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-right text-sm focus:outline-none focus:border-gray-400"
                />
              </div>
            )}

            <Toggle enabled={warrantyEnabled} onChange={handleWarrantyToggle} />
          </div>
        </div>

      </div>
    </div>
  );
}
